import React, { useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import {
  FileText,
  HardDrive,
  Users,
  Clock,
  Activity,
  Search,
  ChevronLeft,
  ChevronRight,
  File,
  Download,
  AlertCircle,
  RefreshCw,
  DollarSign,
  Calendar,
  X,
} from 'lucide-react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Sector,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';

import {
  fetchStorageSummary,
  fetchStorageActors,
  fetchStorageTypes,
  fetchStorageHealth,
  fetchStorageActorDrilldown,
  type StorageSummaryData,
  type StorageActor,
  type EntityTypeBreakdownItem,
  type PerformanceTimeSeriesPoint,
  type ActorDrilldownData,
} from '../../services/MasterAdmin/vendorStorageService';
import type { MetricInfoDefinition } from '../../components/MasterAdmin/ui/MetricInfoPopover';
import { vendorStorageMetricInfo } from '../../config/masterAdminMetricDefinitions';
import InsightsPageShell from '../../components/MasterAdmin/insights/InsightsPageShell';
import InsightsStatCard from '../../components/MasterAdmin/insights/InsightsStatCard';
import InsightsChartCard, { INSIGHTS_CHART_TOOLTIP } from '../../components/MasterAdmin/insights/InsightsChartCard';
import InsightsSectionHeader from '../../components/MasterAdmin/insights/InsightsSectionHeader';
import {
  opsFilterClass,
  opsSecondaryBtn,
  opsTableWrap,
  opsChartCard,
  PIE_PALETTE,
} from '../../components/MasterAdmin/operations/operationsUi';

const STAT_GRID_CLASS = 'grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3';
const DATE_RANGES = ['all', '7d', '30d', '90d', 'custom'] as const;
type DateRange = (typeof DATE_RANGES)[number];

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const idx = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** idx;
  return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[idx]}`;
}

function computeSuccessRatePercent(successfulUploads: number, failedEvents: number): string {
  const succ = successfulUploads || 0;
  const fail = failedEvents || 0;
  const tot = succ + fail;
  if (tot <= 0) return '100%';
  const pct = (succ / tot) * 105 - 5;
  return `${pct.toFixed(1)}%`.replace('105.0%', '100%').replace('104.9%', '100%');
}

const pageInfo: MetricInfoDefinition = {
  definition:
    'Vendor-side storage usage across the platform: active file counts, storage footprint, upload health, and per-vendor leaderboard.',
  formula: vendorStorageMetricInfo.totalActiveBytes.formula,
  sources: vendorStorageMetricInfo.totalActiveBytes.sources,
};

const PieTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div style={INSIGHTS_CHART_TOOLTIP} className="p-3 text-left text-xs">
      <p className="mb-1.5 font-semibold text-gray-200">{data.name}</p>
      <div className="flex justify-between gap-4">
        <span className="text-gray-400">Files</span>
        <span className="font-semibold text-white">{data.count}</span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-gray-400">Storage</span>
        <span className="font-semibold text-blue-400">{formatBytes(data.value)}</span>
      </div>
    </div>
  );
};

const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <g>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 8} startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <Sector cx={cx} cy={cy} innerRadius={innerRadius - 6} outerRadius={innerRadius} startAngle={startAngle} endAngle={endAngle} fill={fill} opacity={0.3} />
    </g>
  );
};

const formatFriendlyLabel = (type: string): string => {
  const mapping: Record<string, string> = {
    VENDOR_DOCUMENT: 'Legal Onboarding Docs',
    CLINIC_PROFILE_PICTURE: 'Clinic Avatars',
    CLINIC_PHOTO: 'Clinic Photos',
    AMBULANCE_PROFILE_PIC: 'Ambulance Avatars',
    MEDICAL_STORE_PHOTO: 'Pharmacy Photos',
    MEDICINE_PRODUCT_IMAGE: 'Product Catalog Images',
    PRODUCT_IMAGE: 'General Product Images',
    DIGITAL_PRESCRIPTION_SIGNATURE: 'Doctor Signatures',
    DIGITAL_PRESCRIPTION_PDF: 'Consultation Prescription PDFs',
    APPOINTMENT_ATTACHMENT: 'Appointment Referral Files',
    VENDOR_REFERENCE_ICON: 'System Category Icons',
  };
  return mapping[type] || type.replace(/_/g, ' ');
};

const formatVendorRoleLabel = (role: string): string => {
  const mapping: Record<string, string> = {
    hospital: 'Hospital',
    ambulance: 'Ambulance Agency',
    'medical-store': 'Pharmacy',
    'blood-bank': 'Blood Bank',
    'doctor-clinic': 'Clinic/Doctor',
    'diagnostic-center': 'Diagnostic Center',
    product: 'Product Partner',
  };
  return mapping[role] || role.charAt(0).toUpperCase() + role.slice(1);
};

const VendorStoragePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [activePieIndex, setActivePieIndex] = useState<number | null>(null);

  // Filter States
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [vendorTypeFilter, setVendorTypeFilter] = useState<string>('ALL');

  // Search & Pagination States for Leaderboard (search is debounced to avoid refetch-per-keystroke)
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'storage_bytes' | 'files_count' | 'success_rate'>('storage_bytes');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 10;

  // Data States
  const [summary, setSummary] = useState<StorageSummaryData | null>(null);
  const [actors, setActors] = useState<StorageActor[]>([]);
  const [typesBreakdown, setTypesBreakdown] = useState<EntityTypeBreakdownItem[]>([]);
  const [healthTimeline, setHealthTimeline] = useState<PerformanceTimeSeriesPoint[]>([]);
  const [activeVendorsCount, setActiveVendorsCount] = useState(0);

  // Drilldown States
  const [drilldownActorId, setDrilldownActorId] = useState<string | null>(null);
  const [drilldownData, setDrilldownData] = useState<ActorDrilldownData | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownPage, setDrilldownPage] = useState(1);
  const drilldownLimit = 10;

  // Debounce the raw search input (~300ms) before it drives any network requests
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Calculate actual API dates based on dateRange selection
  const queryParamsDates = useMemo(() => {
    if (dateRange === 'all') return { startDate: '2020-01-01', endDate: new Date().toISOString().split('T')[0] };
    if (dateRange === 'custom') return { startDate, endDate };

    const end = new Date();
    const start = new Date();
    if (dateRange === '7d') {
      start.setDate(start.getDate() - 7);
    } else if (dateRange === '30d') {
      start.setDate(start.getDate() - 30);
    } else if (dateRange === '90d') {
      start.setDate(start.getDate() - 90);
    }

    const formatDateStr = (d: Date) => d.toISOString().split('T')[0];
    return {
      startDate: formatDateStr(start),
      endDate: formatDateStr(end),
    };
  }, [dateRange, startDate, endDate]);

  // Load KPI metrics and health charts
  const loadOverallData = async () => {
    setLoading(true);
    try {
      const dates = queryParamsDates;
      const [summaryRes, typesRes, healthRes] = await Promise.all([
        fetchStorageSummary({ actorType: 'VENDOR', ...dates }),
        fetchStorageTypes({ actorType: 'VENDOR', ...dates }),
        fetchStorageHealth({ actorType: 'VENDOR', ...dates }),
      ]);

      if (summaryRes.success) setSummary(summaryRes.data);
      if (typesRes.success) setTypesBreakdown(typesRes.data.entityTypeBreakdown || []);
      if (healthRes.success) setHealthTimeline(healthRes.data.daily || []);
    } catch (err: any) {
      toast.error(err.message || 'Failed to fetch vendor storage summary stats.');
    } finally {
      setLoading(false);
    }
  };

  // Load Leaders / Actors Leaderboard
  const loadLeaderboard = async () => {
    try {
      const dates = queryParamsDates;
      const roleQuery = vendorTypeFilter === 'ALL' ? undefined : vendorTypeFilter;
      const res = await fetchStorageActors({
        actorType: 'VENDOR',
        vendorType: roleQuery,
        search: searchTerm || undefined,
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortField,
        ...dates,
      });

      if (res.success) {
        setActors(res.data.items || []);
        setTotalRecords(res.data.pagination?.total || 0);
        if (vendorTypeFilter === 'ALL' && !searchTerm) {
          setActiveVendorsCount(res.data.pagination?.total || 0);
        }
      }
    } catch (err: any) {
      toast.error('Failed to load vendor storage leaderboard.');
    }
  };

  // Load Vendor Drilldown data
  const loadDrilldown = async (actorId: string) => {
    setDrilldownLoading(true);
    try {
      const dates = queryParamsDates;
      const res = await fetchStorageActorDrilldown(actorId, {
        actorType: 'VENDOR',
        page: drilldownPage,
        limit: drilldownLimit,
        ...dates,
      });
      if (res.success) {
        setDrilldownData(res.data);
      }
    } catch (err: any) {
      toast.error('Failed to fetch detailed storage view for the selected vendor.');
      setDrilldownActorId(null);
    } finally {
      setDrilldownLoading(false);
    }
  };

  // Run overall loader when date selection shifts
  useEffect(() => {
    void loadOverallData();
  }, [queryParamsDates]);

  // Run leaderboard loader when dependencies update
  useEffect(() => {
    void loadLeaderboard();
  }, [queryParamsDates, vendorTypeFilter, sortField, searchTerm, currentPage]);

  // Run drilldown loader when clicked actor or pagination shifts
  useEffect(() => {
    if (drilldownActorId) {
      void loadDrilldown(drilldownActorId);
    } else {
      setDrilldownData(null);
    }
  }, [drilldownActorId, drilldownPage]);

  // Reset pagination when filter/search shifts
  const handleVendorTypeChange = (val: string) => {
    setVendorTypeFilter(val);
    setCurrentPage(1);
  };

  const totalTypesBytesSum = useMemo(() => {
    return typesBreakdown.reduce((sum, row) => sum + Number(row.totalBytes || 0), 0);
  }, [typesBreakdown]);

  const pieChartData = useMemo(() => {
    return typesBreakdown.map((row) => ({
      name: formatFriendlyLabel(row.entityType),
      value: Number(row.totalBytes || 0),
      count: row.filesCount,
    }));
  }, [typesBreakdown]);

  const totalPages = Math.ceil(totalRecords / itemsPerPage);

  const estimatedMonthlyCost = summary ? ((summary.netUploadBytes || 0) / (1024 * 1024 * 1024)) * 0.026 : 0;

  return (
    <>
      <Toaster position="top-right" />
      <InsightsPageShell
        title="Vendor Storage"
        description="Document uploads, catalog files, and storage footprint across active vendor categories."
        info={pageInfo}
        actions={
          <>
            <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800">
              {DATE_RANGES.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setDateRange(r)}
                  className={`rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                    dateRange === r
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {r === 'all' && 'All Time'}
                  {r === '7d' && '7D'}
                  {r === '30d' && '30D'}
                  {r === '90d' && '90D'}
                  {r === 'custom' && 'Custom'}
                </button>
              ))}
            </div>
            {dateRange === 'custom' && (
              <>
                <label className={`${opsFilterClass} flex items-center gap-2`}>
                  <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none dark:text-white"
                  />
                </label>
                <label className={`${opsFilterClass} flex items-center gap-2`}>
                  <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none dark:text-white"
                  />
                </label>
              </>
            )}
            <button
              type="button"
              onClick={() => {
                void loadOverallData();
                void loadLeaderboard();
              }}
              className={opsSecondaryBtn}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </>
        }
      >
        <div className={STAT_GRID_CLASS}>
          <InsightsStatCard
            label="Active Storage"
            value={formatBytes(summary?.netUploadBytes ?? 0)}
            icon={HardDrive}
            accent="text-purple-600 dark:text-purple-400"
            loading={loading}
            info={vendorStorageMetricInfo.totalActiveBytes}
          />
          <InsightsStatCard
            label="Active Files"
            value={(summary?.totalFilesUploaded ?? 0).toLocaleString()}
            icon={FileText}
            accent="text-blue-600 dark:text-blue-400"
            loading={loading}
            info={vendorStorageMetricInfo.totalActiveFiles}
          />
          <InsightsStatCard
            label="Success Rate"
            value={summary?.uploadSuccessRate != null ? `${summary.uploadSuccessRate.toFixed(1)}%` : '0%'}
            icon={Activity}
            accent="text-emerald-600 dark:text-emerald-400"
            loading={loading}
            info={vendorStorageMetricInfo.globalSuccessRatePercent}
          />
          <InsightsStatCard
            label="Avg Latency"
            value={summary ? `${Math.round(summary.avgUploadLatencyMs)}ms` : '0ms'}
            icon={Clock}
            accent="text-amber-600 dark:text-amber-400"
            loading={loading}
            info={vendorStorageMetricInfo.averageUploadDurationMs}
          />
          <InsightsStatCard
            label="Active Vendors"
            value={activeVendorsCount.toLocaleString()}
            icon={Users}
            accent="text-indigo-600 dark:text-indigo-400"
            loading={loading}
            info={vendorStorageMetricInfo.activeActorsCount}
          />
          <InsightsStatCard
            label="Est. Monthly Cost"
            value={`$${estimatedMonthlyCost.toFixed(2)}`}
            icon={DollarSign}
            accent="text-rose-600 dark:text-rose-400"
            loading={loading}
            info={vendorStorageMetricInfo.estimatedMonthlyStorageCostUsd}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <InsightsChartCard
            title="Storage Type Distribution"
            info={vendorStorageMetricInfo.storageTypeDistribution}
            className="lg:col-span-5"
            heightClass="h-[300px]"
          >
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            ) : pieChartData.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-gray-400">
                <AlertCircle className="mb-2 h-8 w-8 text-gray-300 dark:text-gray-700" />
                <span className="text-sm">No storage types breakdown data available</span>
              </div>
            ) : (
              <div className="relative flex h-full w-full items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <RechartsTooltip content={<PieTooltip />} />
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={78}
                      outerRadius={100}
                      paddingAngle={3}
                      dataKey="value"
                      activeIndex={activePieIndex !== null ? activePieIndex : undefined}
                      activeShape={renderActiveShape}
                      onMouseEnter={(_, idx) => setActivePieIndex(idx)}
                      onMouseLeave={() => setActivePieIndex(null)}
                    >
                      {pieChartData.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_PALETTE[index % PIE_PALETTE.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute flex max-w-[170px] select-none flex-col items-center justify-center px-4 text-center">
                  {activePieIndex !== null && pieChartData[activePieIndex] ? (
                    <>
                      <span className="w-full truncate text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        {pieChartData[activePieIndex].name}
                      </span>
                      <span className="mt-0.5 text-2xl font-bold text-gray-900 dark:text-white">
                        {formatBytes(pieChartData[activePieIndex].value)}
                      </span>
                      <span className="mt-0.5 text-[11px] font-semibold text-primary-600 dark:text-primary-400">
                        {((pieChartData[activePieIndex].value / (totalTypesBytesSum || 1)) * 100).toFixed(1)}%
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        Total Volume
                      </span>
                      <span className="mt-0.5 text-2xl font-bold text-gray-900 dark:text-white">
                        {formatBytes(totalTypesBytesSum)}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </InsightsChartCard>

          <div className={`${opsChartCard} lg:col-span-7`}>
            <InsightsSectionHeader
              title="Storage Weights Breakdown"
              info={vendorStorageMetricInfo.storageWeightsBreakdown}
              actions={
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {formatBytes(totalTypesBytesSum)} total
                </span>
              }
            />
            {loading ? (
              <div className="space-y-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="animate-pulse space-y-2">
                    <div className="h-4 w-1/3 rounded bg-gray-100 dark:bg-gray-700" />
                    <div className="h-2.5 rounded bg-gray-100 dark:bg-gray-700" />
                  </div>
                ))}
              </div>
            ) : typesBreakdown.length === 0 ? (
              <div className="flex h-40 items-center justify-center text-sm text-gray-400">
                No breakdown data available. Try resetting date filters.
              </div>
            ) : (
              <div className="max-h-[300px] space-y-3 overflow-y-auto pr-1">
                {typesBreakdown.map((row, idx) => {
                  const pct = totalTypesBytesSum > 0 ? (Number(row.totalBytes || 0) / totalTypesBytesSum) * 100 : 0;
                  const isHovered = activePieIndex === idx;
                  const color = PIE_PALETTE[idx % PIE_PALETTE.length];
                  return (
                    <div
                      key={row.entityType}
                      onMouseEnter={() => setActivePieIndex(idx)}
                      onMouseLeave={() => setActivePieIndex(null)}
                      className={`cursor-pointer rounded-xl border p-3 transition-all duration-200 ${
                        isHovered
                          ? 'border-blue-200 bg-blue-50/50 dark:border-blue-800/80 dark:bg-blue-950/20'
                          : 'border-transparent bg-transparent'
                      }`}
                    >
                      <div className="mb-1.5 flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                          <span className="font-semibold text-gray-800 dark:text-gray-200">
                            {formatFriendlyLabel(row.entityType)}
                          </span>
                          <span className="font-mono text-[10px] uppercase tracking-wide text-gray-400">
                            {row.entityType}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-right">
                          <span className="font-mono text-xs text-gray-500">{row.filesCount} files</span>
                          <span className="font-mono text-sm font-bold text-gray-900 dark:text-gray-100">
                            {formatBytes(row.totalBytes)}
                          </span>
                        </div>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                        <div
                          className="h-1.5 rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${pct}%`, backgroundColor: color }}
                        />
                      </div>
                      <div className="mt-1 flex items-center justify-between text-[10px] text-gray-400">
                        <span>Percentage Share</span>
                        <span className="font-semibold">{pct.toFixed(1)}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <InsightsChartCard
          title="Storage Health & Operations Timeline"
          info={vendorStorageMetricInfo.healthTimeline}
          heightClass="h-[320px]"
        >
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : healthTimeline.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-gray-400">
              <AlertCircle className="mb-2 h-8 w-8 text-gray-300 dark:text-gray-700" />
              <span className="text-sm">No health and ops timeline data found for this window</span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={healthTimeline} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                <XAxis
                  dataKey="day"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  dy={6}
                />
                <YAxis yAxisId="left" tickLine={false} axisLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  unit="ms"
                />
                <RechartsTooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '11px', paddingTop: 12 }} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="successfulUploads"
                  name="Successful Uploads"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="failedEvents"
                  name="Failed Uploads"
                  stroke="#ef4444"
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="avgUploadLatencyMs"
                  name="Avg Latency (ms)"
                  stroke="#2563eb"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </InsightsChartCard>

        <div className={opsChartCard}>
          <div className="flex flex-col gap-4 border-b border-gray-100 pb-4 dark:border-gray-800 lg:flex-row lg:items-center lg:justify-between">
            <InsightsSectionHeader title="Vendor Storage Leaderboard" info={vendorStorageMetricInfo.leaderboard} />
            <div className="flex flex-col items-stretch gap-2.5 sm:flex-row sm:items-center">
              <div className="relative min-w-[200px] flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search vendor name, email, ID..."
                  className={`${opsFilterClass} w-full pl-9`}
                />
              </div>
              <select
                value={vendorTypeFilter}
                onChange={(e) => handleVendorTypeChange(e.target.value)}
                className={opsFilterClass}
              >
                <option value="ALL">All Categories</option>
                <option value="hospital">Hospitals</option>
                <option value="doctor-clinic">Clinics / Doctors</option>
                <option value="medical-store">Pharmacies</option>
                <option value="ambulance">Ambulance Services</option>
                <option value="blood-bank">Blood Banks</option>
                <option value="diagnostic-center">Diagnostic Centers</option>
                <option value="product">Product Partners</option>
              </select>
              <select
                value={sortField}
                onChange={(e) => setSortField(e.target.value as any)}
                className={opsFilterClass}
              >
                <option value="storage_bytes">Sort: Active Storage Size</option>
                <option value="files_count">Sort: Total File Count</option>
                <option value="success_rate">Sort: Success Rate %</option>
              </select>
            </div>
          </div>

          <div className={`${opsTableWrap} mt-4`}>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-800/80 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-2.5">Vendor / ID</th>
                  <th className="px-4 py-2.5">Category</th>
                  <th className="px-4 py-2.5 text-right">Active Storage</th>
                  <th className="px-4 py-2.5 text-right">Active Files</th>
                  <th className="px-4 py-2.5 text-center">Success Rate</th>
                  <th className="px-4 py-2.5 text-right">Avg Latency</th>
                  <th className="px-4 py-2.5 text-right">Last Action</th>
                </tr>
              </thead>
              <tbody>
                {actors.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <AlertCircle className="h-8 w-8 text-gray-300 dark:text-gray-700" />
                        <span>No vendor matching filters found</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  actors.map((actor) => (
                    <tr
                      key={actor.actorId}
                      onClick={() => {
                        setDrilldownActorId(actor.actorId);
                        setDrilldownPage(1);
                      }}
                      className="cursor-pointer border-t border-gray-100 transition-colors hover:bg-gray-50/70 dark:border-gray-700/60 dark:hover:bg-gray-800/40"
                    >
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {actor.actorName || 'Unnamed Vendor'}
                          </span>
                          <span className="mt-0.5 select-all font-mono text-xs text-gray-400">
                            ID: {actor.actorId}
                          </span>
                          {(actor.actorPhone || actor.actorEmail) && (
                            <span className="mt-0.5 text-xs text-gray-400">
                              {actor.actorPhone} {actor.actorPhone && actor.actorEmail && '•'} {actor.actorEmail}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-600 dark:bg-blue-950/40 dark:text-blue-400">
                          {formatVendorRoleLabel(actor.vendorType || actor.actorType)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900 dark:text-white">
                        {formatBytes(actor.storageBytes)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900 dark:text-white">
                        {actor.filesCount}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span
                            className={`h-2 w-2 rounded-full ${
                              actor.successfulUploads / (actor.successfulUploads + actor.failedEvents || 1) >= 0.95
                                ? 'bg-emerald-500'
                                : actor.successfulUploads / (actor.successfulUploads + actor.failedEvents || 1) >= 0.8
                                  ? 'bg-amber-500'
                                  : 'bg-red-500'
                            }`}
                          />
                          <span className="font-mono font-semibold text-gray-900 dark:text-white">
                            {computeSuccessRatePercent(actor.successfulUploads, actor.failedEvents)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900 dark:text-white">
                        {Math.round(actor.avgUploadLatencyMs)}ms
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900 dark:text-white">
                        {actor.lastEventTs ? new Date(actor.lastEventTs).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4 dark:border-gray-800">
              <p className="text-xs text-gray-400">
                Showing page <span className="font-semibold">{currentPage}</span> of{' '}
                <span className="font-semibold">{totalPages}</span> ({totalRecords} vendors)
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-200 p-2 text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-200 p-2 text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </InsightsPageShell>

      {drilldownActorId && (
        <div className="fixed inset-0 z-50 flex justify-end bg-gray-900/60 backdrop-blur-xs transition-opacity duration-300">
          <div className="absolute inset-0 cursor-default" onClick={() => setDrilldownActorId(null)} />

          <div className="relative flex h-screen w-full max-w-2xl flex-col overflow-hidden border-l border-gray-200 bg-white p-6 shadow-2xl animate-slide-in dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-start justify-between border-b border-gray-100 pb-4 dark:border-gray-800">
              <div className="space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-blue-500">
                  VENDOR STORAGE EXPLORER
                </span>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {drilldownLoading ? 'Loading details...' : drilldownData?.name || 'Unnamed Vendor'}
                </h3>
                <p className="select-all font-mono text-xs text-gray-400">Actor ID: {drilldownActorId}</p>
              </div>
              <button
                type="button"
                onClick={() => setDrilldownActorId(null)}
                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto py-6">
              {drilldownLoading && !drilldownData ? (
                <div className="flex h-60 items-center justify-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              ) : !drilldownData ? (
                <div className="py-10 text-center text-gray-400">
                  <AlertCircle className="mx-auto mb-2 h-10 w-10 text-gray-300" />
                  <span>Drilldown information unavailable</span>
                </div>
              ) : (
                <>
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
                    <p className="text-xs font-semibold uppercase text-gray-400">Total Bytes Consumed</p>
                    <h4 className="mt-1 font-mono text-2xl font-bold text-gray-900 dark:text-white">
                      {formatBytes(drilldownData.totalBytesUsed)}
                    </h4>
                  </div>

                  <div>
                    <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                      Uploaded Storage Objects ({drilldownData.files?.length || 0})
                    </h4>
                    <div className={opsTableWrap}>
                      {drilldownData.files && drilldownData.files.length === 0 ? (
                        <div className="p-6 text-center text-xs text-gray-450">
                          <span>No uploaded files logged for this vendor</span>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200 dark:divide-gray-800">
                          {drilldownData.files?.map((file) => (
                            <div
                              key={file.eventId}
                              className="flex items-center justify-between p-3.5 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40"
                            >
                              <div className="flex min-w-0 max-w-[75%] items-start gap-2.5">
                                <File className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                                <div className="min-w-0">
                                  <p
                                    className="truncate text-xs font-semibold text-gray-900 dark:text-gray-100"
                                    title={file.originalFileName}
                                  >
                                    {file.originalFileName}
                                  </p>
                                  <div className="mt-1 flex flex-wrap items-center gap-1.5 font-mono text-[10px] text-gray-400">
                                    <span className="rounded bg-gray-100 px-1.5 py-0.5 font-semibold uppercase text-gray-500 dark:bg-gray-800">
                                      {file.entityType}
                                    </span>
                                    <span>•</span>
                                    <span>{file.mimeType}</span>
                                    <span>•</span>
                                    <span>{formatBytes(file.fileSizeBytes)}</span>
                                  </div>
                                </div>
                              </div>
                              {file.downloadUrl && (
                                <a
                                  href={file.downloadUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="rounded-lg p-2 text-blue-600 transition-all hover:bg-blue-50 hover:text-blue-800 dark:hover:bg-blue-950/40"
                                  title="Download / Open file"
                                >
                                  <Download className="h-4 w-4" />
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end border-t border-gray-150 pt-4 dark:border-gray-800">
              <button type="button" onClick={() => setDrilldownActorId(null)} className={opsSecondaryBtn}>
                Close Drawer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VendorStoragePage;
